import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage, useIntl} from 'react-intl';

import {Button} from '@stubhub/bamboo';
import {getCurrencySymbol} from '@stubhub/currency-utils';
import {shstoreIdMappings} from '@stubhub/general-utils';
import LocalizationPicker from '@stubhub/react-localization-picker';
import {getSupportedCurrencies, currenciesMessages} from '@stubhub/react-store-default-currency';
import {connect} from '@stubhub/react-store-provider';
import StoreSelector from '@stubhub/react-store-selector';
import StoreSelectorModal from '@stubhub/react-store-selector-modal';

import controller from './controller';
import LanguageIcon from './language-icon';
import messages from './messages';

import '../styles/localization-selector.scss';

const LocalizationSelector = ({
  isHomepage,
  site: siteCountry,
  shstoreId,
  defaultLocale,
  currentLocale,
  supportedLocales,
  selectedCurrencyCode,
  defaultCurrency,
  isAdvisoryEnabled,
  isLanguageSelectorEnabled,
}) => {
  const intl = useIntl();
  const [supportedCurrencies, setSupportedCurrencies] = useState([]);
  const [showStoreSelectorModal, setShowStoreSelectorModal] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  useEffect(() => {
    const loadSupportedCurrencies = async () => {
      if (isAdvisoryEnabled) {
        const supportedCurrencies = await getSupportedCurrencies();
        setSupportedCurrencies(supportedCurrencies);
      }
    };

    loadSupportedCurrencies();
  }, [isAdvisoryEnabled]);

  const getLanSelector = () => {
    const items = supportedLocales.map((item) => {
      return {
        key: item.locale,
        value: item.locale,
        label: item.languageDisplayName,
      };
    });

    let menu;
    if (items.length > 1) {
      menu = {id: 'language-selector'};
      supportedLocales.some((item) => {
        if (item.locale === currentLocale) {
          menu.label = item.languageDisplayName;

          return true;
        }

        return false;
      });
    }

    return {items, menu};
  };

  const onSegmentClick = (e) => {
    e.stopPropagation();
    setShowPicker(true);
  };

  const clickLocalizationPickerClose = () => {
    setShowPicker(false);
  };

  const clickStoreSelector = () => {
    setShowStoreSelectorModal(true);
  };

  const clickCloseStoreSelector = () => {
    setShowStoreSelectorModal(false);
  };

  const onStoreSelect = (store) => {
    setShowStoreSelectorModal(false);
    window.location.href = store.url;
  };

  const getCurrencyInfo = () => {
    if (selectedCurrencyCode && supportedCurrencies.some((c) => c.key === selectedCurrencyCode)) {
      const domainCurrencyCode = defaultCurrency || 'USD';
      const currencyCode = (__CLIENT__ && selectedCurrencyCode) || domainCurrencyCode;
      const currencySymbol = getCurrencySymbol(currencyCode, defaultLocale);
      const currencyText = intl.formatMessage(currenciesMessages[currencyCode]);

      return {currencySymbol, currencyText};
    }

    return {currencySymbol: '$€¥', currencyText: intl.formatMessage(messages.noCurrency)};
  };

  const getSite = () => {
    if (siteCountry) {
      return siteCountry.toUpperCase();
    }
    const fromMapping = shstoreId && shstoreIdMappings[shstoreId] && shstoreIdMappings[shstoreId].site;
    if (fromMapping) {
      return fromMapping.toUpperCase();
    }

    return defaultLocale.substr(3, 2).toUpperCase();
  };

  const site = getSite();
  const lanSelector = isLanguageSelectorEnabled && getLanSelector();
  const {currencyText, currencySymbol} = getCurrencyInfo();
  const isIE11Browser =
    typeof window !== 'undefined' &&
    typeof document !== 'undefined' &&
    !!window.MSInputMethodContext &&
    !!document.documentMode;
  const showSegmentedLocalizationControls = !isIE11Browser && (!!lanSelector || isAdvisoryEnabled);

  return (
    <div className="social-app">
      <div className="heading">
        <FormattedMessage id="Footer.linkTitle.countries" defaultMessage="Live events all over the world" />
      </div>
      <LocalizationPicker
        visible={showPicker}
        onClose={clickLocalizationPickerClose}
        showLanguageSelector={!!lanSelector}
        showCurrencySelector={isAdvisoryEnabled}
      />

      {isHomepage && (
        <div className="store-selector-wrapper">
          <StoreSelector
            onClick={clickStoreSelector}
            countryCode={site}
            countryName={intl.formatMessage(messages[`domain-${site}`])}
          />
          <StoreSelectorModal
            currentStore={site}
            visible={showStoreSelectorModal}
            onClose={clickCloseStoreSelector}
            onStoreClick={onStoreSelect}
          />
        </div>
      )}

      {showSegmentedLocalizationControls && (
        <Button appearance="secondary" className="segmented-localization-controls" onClickCapture={onSegmentClick}>
          {lanSelector && (
            <span className="lang">
              <LanguageIcon />
              <span className="language-selector">{lanSelector.menu.label}</span>
            </span>
          )}

          {isAdvisoryEnabled && (
            <span className="currency-selector">
              {currencySymbol} · {currencyText}
            </span>
          )}
        </Button>
      )}
    </div>
  );
};

LocalizationSelector.propTypes = {
  /* If we're in the homepage, as store selector is only shown in the homepage */
  isHomepage: PropTypes.bool,

  /* Default currency code */
  defaultCurrency: PropTypes.string,

  /* Default site's locale */
  defaultLocale: PropTypes.string,

  /* Current user chosen locale */
  currentLocale: PropTypes.string,

  /* Supported locales for the site */
  supportedLocales: PropTypes.array,

  /* Site country code to override */
  site: PropTypes.string,

  /* Site id */
  shstoreId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  /* Current selected currency code */
  selectedCurrencyCode: PropTypes.string,

  /* Is advisory FF enabled? */
  isAdvisoryEnabled: PropTypes.bool,

  /* Is language selector enabled? */
  isLanguageSelectorEnabled: PropTypes.bool,
};

export default connect(controller)(LocalizationSelector);
